import { newRelic } from '~/utils/newRelic/newRelic';

import {
  type ScanEventProcessor,
  type ScanEventProcessorOptions,
} from '../types/scanEventProcessor.type';
import { createScanEvent } from './createScanEvent.util';
import { normalizeBarcodeValue } from './normalization.util';

/**
 * Turns queued raw scan events into enriched scan events and hands them to the
 * action handler, one at a time, until the queue is empty. Each event waits for its
 * own context to resolve before the next one is dequeued.
 */
export const createScanEventProcessor = ({
  getScanContext,
  handleScanEvent,
  queue,
}: ScanEventProcessorOptions): ScanEventProcessor => {
  let isDraining = false;
  let needsDrain = false;

  const process = async () => {
    if (isDraining) {
      needsDrain = true;
      return;
    }

    isDraining = true;

    try {
      let rawScanEvent = queue.dequeue();

      while (rawScanEvent) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const scanContext = await getScanContext({
            barcodeValue: normalizeBarcodeValue(rawScanEvent.barcode_value),
            correlationId: rawScanEvent.correlation_id,
          });

          handleScanEvent(createScanEvent(rawScanEvent, scanContext));
        } catch (error) {
          newRelic.noticeError('Failed to process scan event', {
            correlationId: rawScanEvent.correlation_id,
            error,
          });
        }

        rawScanEvent = queue.dequeue();
      }
    } finally {
      isDraining = false;
      if (needsDrain) {
        needsDrain = false;
        await process();
      }
    }
  };

  return { process };
};
